import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import SaveIcon from '@material-ui/icons/Save';
import UndoIcon from '@material-ui/icons/Undo';
import WarningIcon from '@material-ui/icons/Warning';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  toolbarGrow: { flexGrow: 1, minWidth: 240 },
  keyCell: {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    maxWidth: 280,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dirtyRow: {
    backgroundColor: theme.palette.action.hover,
  },
  emptyValue: {
    color: theme.palette.error.main,
    fontStyle: 'italic',
  },
  statusChip: { marginLeft: theme.spacing(1) },
  cellInput: {
    '& .MuiInputBase-root': { fontSize: '0.9rem' },
  },
}));

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'missing_pt', label: 'Missing PT' },
  { value: 'missing_en', label: 'Missing EN' },
  { value: 'missing_any', label: 'Missing any' },
  { value: 'dirty', label: 'Unsaved changes' },
];

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

const isEmpty = (v) => v == null || String(v).trim() === '';

const TranslationsEditor = ({ rows, onSave, saving }) => {
  const classes = useStyles();
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    setEdits({});
    setPage(0);
  }, [rows]);

  const merged = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list.map((r) => {
      const e = edits[r.key];
      return {
        key: r.key,
        pt: e ? e.pt : r.pt || '',
        en: e ? e.en : r.en || '',
        originalPt: r.pt || '',
        originalEn: r.en || '',
        dirty: !!e,
      };
    });
  }, [rows, edits]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return merged.filter((r) => {
      if (filter === 'missing_pt' && !isEmpty(r.pt)) return false;
      if (filter === 'missing_en' && !isEmpty(r.en)) return false;
      if (filter === 'missing_any' && !isEmpty(r.pt) && !isEmpty(r.en)) return false;
      if (filter === 'dirty' && !r.dirty) return false;
      if (!q) return true;
      return (
        r.key.toLowerCase().includes(q) ||
        (r.pt || '').toLowerCase().includes(q) ||
        (r.en || '').toLowerCase().includes(q)
      );
    });
  }, [merged, search, filter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filtered.length, rowsPerPage, page]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const dirtyCount = Object.keys(edits).length;
  const totalRows = merged.length;
  const missingPt = merged.filter((r) => isEmpty(r.pt)).length;
  const missingEn = merged.filter((r) => isEmpty(r.en)).length;

  useEffect(() => {
    if (dirtyCount === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  const setCell = (row, lang, value) => {
    setEdits((prev) => {
      const next = { ...prev };
      const base = next[row.key] || { pt: row.originalPt, en: row.originalEn };
      const updated = { ...base, [lang]: value };
      if (updated.pt === row.originalPt && updated.en === row.originalEn) {
        delete next[row.key];
      } else {
        next[row.key] = updated;
      }
      return next;
    });
  };

  const revertRow = (row) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[row.key];
      return next;
    });
  };

  const handleSave = () => {
    if (dirtyCount === 0) return;
    const payload = Object.entries(edits).map(([key, v]) => ({
      key,
      pt: v.pt,
      en: v.en,
    }));
    onSave(payload);
  };

  const handleDiscardAll = () => {
    if (dirtyCount === 0) return;
    // eslint-disable-next-line no-alert
    if (window.confirm(`Discard ${dirtyCount} unsaved change(s)?`)) {
      setEdits({});
    }
  };

  return (
    <Card>
      <CardHeader
        title="Translations"
        subheader={
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <Typography variant="body2" color="textSecondary">
              {totalRows} keys · PT/EN · live from <code>translations</code> table
            </Typography>
            {missingPt > 0 && (
              <Chip
                size="small"
                color="default"
                icon={<WarningIcon />}
                label={`${missingPt} missing PT`}
                className={classes.statusChip}
              />
            )}
            {missingEn > 0 && (
              <Chip
                size="small"
                color="default"
                icon={<WarningIcon />}
                label={`${missingEn} missing EN`}
                className={classes.statusChip}
              />
            )}
            {dirtyCount > 0 && (
              <Chip
                size="small"
                color="secondary"
                label={`${dirtyCount} unsaved`}
                className={classes.statusChip}
              />
            )}
          </Box>
        }
        action={
          <Box display="flex" alignItems="center" style={{ gap: 8, padding: 8 }}>
            <Button
              startIcon={<UndoIcon />}
              onClick={handleDiscardAll}
              disabled={dirtyCount === 0 || saving}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={dirtyCount === 0 || saving}
            >
              {saving ? 'Saving...' : `Save${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
          </Box>
        }
      />
      <Divider />
      <Box className={classes.toolbar}>
        <TextField
          className={classes.toolbarGrow}
          variant="outlined"
          size="small"
          placeholder="Search key, Portuguese, or English…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          select
          variant="outlined"
          size="small"
          label="Filter"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(0);
          }}
          style={{ minWidth: 180 }}
        >
          {FILTERS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary">
          {filtered.length} of {totalRows} keys shown
        </Typography>
      </Box>
      <Divider />
      <CardContent style={{ padding: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '24%' }}>Key</TableCell>
                <TableCell style={{ width: '36%' }}>Português</TableCell>
                <TableCell style={{ width: '36%' }}>English</TableCell>
                <TableCell style={{ width: '4%' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box py={4}>
                      <Typography color="textSecondary">
                        No translations match your filter.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {paged.map((row) => (
                <TableRow
                  key={row.key}
                  className={row.dirty ? classes.dirtyRow : ''}
                  hover
                >
                  <TableCell className={classes.keyCell}>
                    <Tooltip title={row.key} placement="top">
                      <span>{row.key}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      value={row.pt}
                      onChange={(e) => setCell(row, 'pt', e.target.value)}
                      className={classes.cellInput}
                      error={isEmpty(row.pt)}
                      helperText={isEmpty(row.pt) ? 'empty' : ''}
                      InputProps={{ spellCheck: true, lang: 'pt' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      multiline
                      value={row.en}
                      onChange={(e) => setCell(row, 'en', e.target.value)}
                      className={classes.cellInput}
                      error={isEmpty(row.en)}
                      helperText={isEmpty(row.en) ? 'empty' : ''}
                      InputProps={{ spellCheck: true, lang: 'en' }}
                    />
                  </TableCell>
                  <TableCell padding="none" align="right">
                    {row.dirty && (
                      <Tooltip title="Revert this row">
                        <IconButton
                          size="small"
                          onClick={() => revertRow(row)}
                          disabled={saving}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </CardContent>
    </Card>
  );
};

TranslationsEditor.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      pt: PropTypes.string,
      en: PropTypes.string,
    })
  ),
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

TranslationsEditor.defaultProps = {
  rows: [],
  saving: false,
};

export default TranslationsEditor;
